import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { HeroFade, StaggerGrid, StaggerItem } from "@/lib/motion";
import { useSEO } from "@/hooks/useSEO";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then(({ data }) => setPosts(data)); }, []);

  useSEO({
    title: "Health Tips & Medical Insights Blog",
    description: "Read practical, evidence-based health tips and the latest medical insights from the consulting physicians and health team at Afzal Medical Complex."
  });

  return (
    <div data-testid="blog-page">
      <section className="section bg-red-50/40">
        <div className="container-medi max-w-3xl">
          <HeroFade><div className="overline mb-3">Health Tips</div></HeroFade>
          <HeroFade delay={0.1}><h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-slate-900">Insights from our medical team</h1></HeroFade>
          <HeroFade delay={0.2}><p className="mt-5 text-slate-600 text-lg">Practical, evidence-based tips to help you and your family live healthier.</p></HeroFade>
        </div>
      </section>

      <section className="section">
        <StaggerGrid className="container-medi grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <StaggerItem key={p.id}>
              <Link to={`/blog/${p.slug}`} className="card-medi group block" data-testid={`blog-item-${p.id}`}>
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                </div>
                <div className="p-6">
                  <div className="overline mb-2">{p.category}</div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 leading-snug group-hover:text-red-700 transition">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {p.author}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
          {posts.length === 0 && <p className="text-slate-500">No articles yet.</p>}
        </StaggerGrid>
      </section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setErr("Article not found"));
  }, [slug]);

  useSEO({
    title: post ? post.title : "Health Article",
    description: post ? post.excerpt : "Load health article...",
    schema: post ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "image": post.cover_image,
      "genre": post.category,
      "description": post.excerpt,
      "datePublished": post.created_at,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "publisher": {
        "@type": "MedicalOrganization",
        "name": "Afzal Medical Complex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://afzalmedicalcomplex.com/favicon-180.png"
        }
      }
    } : null
  });

  if (err) return <div className="container-medi section">{err}</div>;
  if (!post) return <div className="container-medi section text-slate-500">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <article className="section">
        <div className="container-medi max-w-3xl">
          <Link to="/blog" className="text-sm text-red-700 inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> All articles</Link>
          <div className="overline mb-3">{post.category}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold text-slate-900 leading-[1.1]">{post.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {post.author}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(post.created_at).toLocaleDateString()}</span>
          </div>
          {post.cover_image && <img src={post.cover_image} alt={post.title} className="mt-8 w-full aspect-[16/9] rounded-3xl object-cover shadow-medi-md" />}
          <div className="prose prose-slate mt-10 max-w-none text-slate-700 whitespace-pre-line leading-relaxed text-lg">
            {post.content}
          </div>
          <div className="mt-12">
            <Link to="/contact" className="btn-primary">Book a consultation <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </article>
    </div>
  );
}
